import axios from "axios";
import { SERVER_URL } from "Constants";
import useAsync from "Hooks/useAsync";
import { accountType } from "Models/Enums";
import moment from "moment";
import { catchErrorAsync } from "Util/CatchErrorAsync";
import { TruncatePrice } from "Util/NumberUtils";
import { ComboboxOption } from "Views/Components/Combobox/Combobox";
import MultiComboboxSetter from "Views/Components/Combobox/MultiComboboxSetter";
import { CreditReportProductDetail } from "Views/Components/CreditReportSettings/CreditReportProductList";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";

async function getAvailableCreditReports(providerId: string, businessEntityId: string, data: {
	lookupValue: string;
	lookupMethod: "ABN" | "ACN" | "NCN";
	countryCode: string;
	billingType: accountType;
}): Promise<{availableProducts: CreditReportProductDetail[], asicExtractDate?: Date}> {
	const response = await axios.post<{availableProducts: CreditReportProductDetail[], asicExtractDate?: Date}>(`${SERVER_URL}/api/credit-report/list-products-for-business-entity/${businessEntityId}/provider/${providerId}`, {
		countryCode: data.countryCode,
		requestEntity: {
			lookupValue: data.lookupValue,
			lookupMethod: data.lookupMethod
		},
		billingType: data.billingType
	});

	return response.data;
}

type Props = {
	providerId: string;
	businessEntityId: string;
	countryCode: string;
	lookupValue: string;
	lookupMethod: "ABN" | "ACN" | "NCN";
	billingType: accountType;
	selectedCreditReports: CreditReportProductDetail[];
	updateSelectedCreditReports: (data: CreditReportProductDetail[]) => void;
}

export const ProviderProductSelection:React.FunctionComponent<Props> = ({
	providerId,
	businessEntityId,
	countryCode,
	lookupValue,
	lookupMethod,
	billingType,
	selectedCreditReports,
	updateSelectedCreditReports
}) => {
	const response = useAsync(async () : Promise<{availableProducts: CreditReportProductDetail[], asicExtractDate?: Date}> => {
		const [error, data] = await catchErrorAsync(getAvailableCreditReports(providerId, businessEntityId, {
			lookupValue,
			lookupMethod,
			countryCode,
			billingType,
		}));

		if (error) {
			return {
				availableProducts: [],
				asicExtractDate: undefined,
			};
		}

		return data;
	}, [businessEntityId, countryCode, lookupValue, lookupMethod, billingType]);

	const availableCreditReportOptions = (): ComboboxOption<string>[] => {
		// Guards
		if (response.type === 'loading') return [];
		if (response.error) return [];
		const data = response.data;
		if (!data) return [];

		// Map to options
		const options = data.availableProducts.map(d => ({
			value: d.id,
			display: `${d.name} - $${TruncatePrice(d.price)}`
		}));

		options.sort((a, b) => a.display.localeCompare(b.display));
		return options;
	}

	if (response.type === 'loading') return <InlineSpinner />;

	if (response.data && response.data.availableProducts.length <= 0) {
		return <p className="alert alert__warning">No credit report products available for the selected business entity.</p>;
	}

	return (
		<>
			{response.data?.asicExtractDate && <p className="alert-box info">ASIC last updated: {moment(response.data?.asicExtractDate).format("MMMM Do YYYY, h:mm:ss a")}</p>}
			<MultiComboboxSetter
				label="Credit report product"
				tooltip="Choose the credit report product to generate"
				value={selectedCreditReports.map(d => d.id)}
				setValue={newValue => {
					const selectedReports = response.data?.availableProducts.filter(d => newValue.includes(d.id));
					if (selectedReports) {
						updateSelectedCreditReports(selectedReports);
					}
				}}
				getOptionValue={(value?: string) => value}
				options={availableCreditReportOptions()}
				searchable
			/>
		</>
	)
}